/**
 * Session Timeout Component
 *
 * Handles session timeout warnings and automatic logout.
 * Provides a user-friendly experience for session management.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, AUTH_EVENTS } from '../contexts/AuthContext';
import config from '../config/environment';
import './SessionTimeout.css';

interface SessionTimeoutProps {
  onExtendSession?: () => void;
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutProps> = ({ onExtendSession }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { isAuthenticated, logout } = useAuth();

  // Calculate remaining time from warning to expiry
  const warningDuration = config.sessionWarningBefore;

  const handleExtendSession = useCallback(() => {
    setShowWarning(false);
    setCountdown(0);
    if (onExtendSession) {
      onExtendSession();
    }
    // Trigger activity to reset session timeout
    window.dispatchEvent(new Event('mousedown'));
  }, [onExtendSession]);

  const handleLogout = useCallback(() => {
    setShowWarning(false);
    setCountdown(0);
    logout();
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowWarning(false);
      return;
    }

    const handleSessionWarning = (event: CustomEvent<{ warning?: boolean; expired?: boolean }>) => {
      if (event.detail?.warning) {
        setShowWarning(true);
        setCountdown(Math.floor(warningDuration / 1000));
      }
      if (event.detail?.expired) {
        setShowWarning(false);
        // Logout is handled by AuthContext
      }
    };

    window.addEventListener(
      AUTH_EVENTS.SESSION_EXPIRED,
      handleSessionWarning as EventListener
    );

    return () => {
      window.removeEventListener(
        AUTH_EVENTS.SESSION_EXPIRED,
        handleSessionWarning as EventListener
      );
    };
  }, [isAuthenticated, warningDuration]);

  // Countdown timer
  useEffect(() => {
    if (!showWarning || countdown <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showWarning, countdown]);

  if (!showWarning || !isAuthenticated) {
    return null;
  }

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const timeDisplay = minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, '0')}`
    : `${seconds} seconds`;

  return (
    <div className="session-timeout-overlay">
      <div className="session-timeout-modal">
        <div className="session-timeout-icon">⏰</div>
        <h2 className="session-timeout-title">Session Expiring Soon</h2>
        <p className="session-timeout-message">
          Your session will expire in <strong>{timeDisplay}</strong> due to inactivity.
        </p>
        <p className="session-timeout-note">
          Would you like to continue working?
        </p>
        <div className="session-timeout-actions">
          <button
            className="session-timeout-extend"
            onClick={handleExtendSession}
          >
            Continue Session
          </button>
          <button
            className="session-timeout-logout"
            onClick={handleLogout}
          >
            Log Out
          </button>
        </div>
        <div className="session-timeout-progress">
          <div
            className="session-timeout-progress-bar"
            style={{
              width: `${(countdown / (warningDuration / 1000)) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Session status indicator
 * Shows a subtle indicator of session status
 */
export const SessionStatusIndicator: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [lastActivity, setLastActivity] = useState<Date>(new Date());

  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      setLastActivity(new Date());
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !user) {
    return null;
  }

  // Calculate time since last activity
  const now = new Date();
  const idleTime = Math.floor((now.getTime() - lastActivity.getTime()) / 1000);
  const idleMinutes = Math.floor(idleTime / 60);

  // Show indicator if idle for more than 5 minutes
  if (idleMinutes < 5) {
    return null;
  }

  return (
    <div className="session-status-indicator" title={`Idle for ${idleMinutes} minutes`}>
      <span className="session-status-icon">💤</span>
      <span className="session-status-text">Idle: {idleMinutes}m</span>
    </div>
  );
};

export default SessionTimeoutWarning;
